// Lightweight, dependency-free JWT payload reader. We only ever inspect
// non-sensitive claims the FE already trusts the server for (currently
// tenantId, to bucket per-tenant UI state) — signature verification is the
// backend's job on every request, so decoding client-side is safe here.

import { STORAGE_KEY as AUTH_STORAGE_KEY } from '@/stores/auth-store'

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    // atob → binary string → UTF-8 safe JSON
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    const parsed = JSON.parse(json)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/** Extract the tenantId claim from a JWT, or null if absent/undecodable. */
export function decodeTenantId(token: string | null | undefined): string | null {
  if (!token) return null
  const payload = decodeJwtPayload(token)
  const tid = payload?.tenantId
  return typeof tid === 'string' && tid ? tid : null
}

/**
 * Read the current tenantId synchronously from the persisted auth token in
 * localStorage. For callers outside React render (e.g. layout-store's
 * imperative setCurrentTeam) that have no access to the auth store context.
 */
export function getCurrentTenantId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as { token?: string }
    return decodeTenantId(data.token)
  } catch {
    return null
  }
}
